import React, { useEffect } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import { FEATURE_FLAGS, featureFlagMenuAvailable, unreleasedFeatureFlags } from './featureFlags';
import { useFeatureFlags } from './FeatureFlagContext';

export default function FeatureFlagOverlay(): JSX.Element | null {
  const { isEnabled, setEnabled, storageError, overlayOpen, closeOverlay, toggleOverlay } =
    useFeatureFlags();
  const available = featureFlagMenuAvailable();

  useEffect(() => {
    if (!available) return;

    function handleKeyDown(event: KeyboardEvent): void {
      if (
        event.ctrlKey &&
        !event.altKey &&
        !event.metaKey &&
        event.shiftKey &&
        event.key.toLowerCase() === 'f' &&
        !event.repeat
      ) {
        event.preventDefault();
        toggleOverlay();
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [available, toggleOverlay]);

  if (!available) return null;

  return (
    <Dialog
      open={overlayOpen}
      onClose={closeOverlay}
      fullWidth
      maxWidth="sm"
      aria-labelledby="feature-flags-title"
    >
      <DialogTitle id="feature-flags-title">Features in development</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Ctrl + Shift + F opens or closes this panel. Your choices are saved in this browser.
        </Typography>
        {unreleasedFeatureFlags().map((flag) => (
          <FormControlLabel
            key={flag}
            label={FEATURE_FLAGS[flag].label}
            control={
              <Switch
                checked={isEnabled(flag)}
                onChange={(event) => setEnabled(flag, event.target.checked)}
              />
            }
          />
        ))}
        {storageError && (
          <Alert severity="warning">
            Your choice could not be saved in this browser. It will reset after a reload.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeOverlay}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
